import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, MapPin, Building2, Baby, TrendingUp, Coins } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

/**
 * Phase 84.22 · Introduces the "Priya from Dubai" demo persona.
 *
 * Fires:
 *   • Right after a successful demo seed (from Settings or the welcome tour)
 *   • Anytime the user clicks "About the persona" in Settings
 *
 * CTA takes the user to the Dashboard where the story unfolds via
 * Priya's pre-baked AI insights + real screens populated with her data.
 */
export function PriyaIntroModal({ open, onOpenChange, onExplore }) {
  const navigate = useNavigate();

  const handleExplore = () => {
    onExplore?.();
    onOpenChange?.(false);
    navigate('/');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl"
        data-testid="priya-intro-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary mb-1">
            <Sparkles className="h-5 w-5" aria-hidden="true" />
            <span className="text-[11px] uppercase tracking-widest font-semibold">Demo persona</span>
          </div>
          <DialogTitle className="text-2xl">Meet Priya from Dubai</DialogTitle>
          <DialogDescription className="sr-only">
            Priya is a demo persona used to preview 2120 with realistic financial data. She lives in Dubai with her family and manages accounts in AED, INR and USD.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-4 text-sm leading-relaxed text-muted-foreground">
          <p>
            <span className="text-foreground font-medium">Priya</span> is an Indian marketing manager living in Dubai
            with her husband and their 3-year-old son <span className="text-foreground font-medium">Nathan</span>.
            Her life spans two countries — a story many 2120 users know well.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-start gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
              <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold text-foreground">Earns in AED</div>
                <div className="text-[11px]">Salary lands in her Emirates NBD current account each month.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
              <Building2 className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold text-foreground">Home in Bangalore</div>
                <div className="text-[11px]">HDFC home loan &mdash; ₹1.10 Cr outstanding, 15 years to go.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
              <TrendingUp className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold text-foreground">Multi-currency wealth</div>
                <div className="text-[11px]">Indian mutual funds, US ETFs, Infy stock — one clear net worth view.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-muted/40 px-3 py-2.5">
              <Coins className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold text-foreground">Toyota RAV4 EMI</div>
                <div className="text-[11px]">2 years into a 5-year AED auto loan.</div>
              </div>
            </div>
            <div className="flex items-start gap-3 rounded-xl bg-muted/40 px-3 py-2.5 md:col-span-2">
              <Baby className="h-4 w-4 text-primary mt-0.5 shrink-0" aria-hidden="true" />
              <div>
                <div className="text-xs font-semibold text-foreground">Nathan&rsquo;s future</div>
                <div className="text-[11px]">
                  Emergency fund, closing the car loan early, and a 15-year college goal for Nathan.
                </div>
              </div>
            </div>
          </div>

          <p className="pt-1">
            Explore Priya&rsquo;s Dashboard, Wealth, Recurring, Insights and Goals pages — every screen has real,
            interconnected data so you can see how 2120 turns a two-country financial life into one clear picture.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 justify-end mt-4">
          <Button variant="outline" onClick={() => onOpenChange?.(false)} data-testid="priya-intro-close">
            Maybe later
          </Button>
          <Button onClick={handleExplore} data-testid="priya-intro-explore">
            View how 2120 helps Priya get clarity
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
